import storage from "@/utils/storage";
import { Status } from "@/types/product";
import { Order } from "@/types/order";
import { Table } from "@tanstack/react-table";
import { TableView } from "..";
import { getAuthenticatedPages } from "@/utils/helpers";
import { OrderItemWithCurrency } from "../items-table/hooks";
import { TableFilter } from "@/types/utils";

const useHeader = ({
  table,
  view,
  setView,
}: {
  table: Table<Order> | Table<OrderItemWithCurrency>;
  view: TableView;
  setView: (view: TableView) => void;
}) => {
  const currentStatusFilter =
    view === "orders" ? table.getColumn("status")?.getFilterValue() : undefined;

  const isBrandPage = getAuthenticatedPages().brand;

  const onOrdersClick = () => {
    setView("orders");
    if (isBrandPage) {
      storage.setItem("brand_orders_table_view", "orders");
    } else {
      storage.setItem("orders_table_view", "orders");
    }
  };
  const onItemsClick = () => {
    setView("items");
    if (isBrandPage) {
      storage.setItem("brand_orders_table_view", "items");
    } else {
      storage.setItem("orders_table_view", "items");
    }
  };

  const onStatusFilterClick = (status?: Status) => {
    if (view !== "orders") return;
    const storageKey = isBrandPage ? "brand_orders_filter" : "orders_filter";
    const existingFilter = (storage.getItem(storageKey) as TableFilter) || {};

    if (status) {
      table.getColumn("status")?.setFilterValue(status);
      storage.setItem(storageKey, { ...existingFilter, status });
    } else {
      table.getColumn("status")?.setFilterValue("");
      if (Object.keys(existingFilter).length === 1) {
        storage.removeItem(storageKey);
      } else {
        delete existingFilter.status;
        storage.setItem(storageKey, existingFilter);
      }
    }
  };

  let totalSales = 0;
  let currency = "GEL";

  function isOrder(row: Order | OrderItemWithCurrency): row is Order {
    return (row as Order).items !== undefined;
  }

  function isOrderItem(
    row: Order | OrderItemWithCurrency
  ): row is OrderItemWithCurrency {
    return (row as OrderItemWithCurrency).unit_price !== undefined;
  }

  const rows = table.getRowModel().rows;

  if (view === "orders") {
    totalSales = rows
      .flatMap((row) => (isOrder(row.original) ? row.original.items : []))
      .reduce((sum, item) => sum + (item?.unit_price || 0), 0);

    const orderRow = rows.find((row) => isOrder(row.original));
    currency =
      orderRow && isOrder(orderRow.original)
        ? orderRow.original.currency_code || "GEL"
        : "GEL";
  } else if (view === "items") {
    totalSales = rows
      .filter((row) => isOrderItem(row.original))
      .reduce(
        (sum, row) =>
          isOrderItem(row.original)
            ? sum + (row.original.unit_price || 0)
            : sum,
        0
      );

    const itemRow = rows.find((row) => isOrderItem(row.original));
    currency =
      itemRow && isOrderItem(itemRow.original)
        ? itemRow.original.currency_code || "GEL"
        : "GEL";
  }

  return {
    currentStatusFilter,
    onOrdersClick,
    onItemsClick,
    onStatusFilterClick,
    totalSales,
    currency,
  };
};

export { useHeader };
